import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  collection, 
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL
} from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { mealDeskVendorsService } from '../firebase/services';
import { 
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  HeartIcon,
  FireIcon,
  CloudArrowUpIcon,
  ShoppingCartIcon,
  TagIcon,
  CalculatorIcon,
  BookmarkIcon,
  ClipboardDocumentListIcon,
  StarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const MenuEditor = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');

  const [activeTab, setActiveTab] = useState('daily'); // 'daily' or 'master'
  const [activeCategory, setActiveCategory] = useState('mainCourse');
  
  // Menu data
  const [dailyMenus, setDailyMenus] = useState([]);
  const [masterMenuItems, setMasterMenuItems] = useState([]);
  const [allMenuItems, setAllMenuItems] = useState([]); // For combo selection
  
  // Form states
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showComboBuilder, setShowComboBuilder] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    imageUrl: '',
    veg: true,
    calories: '',
    isCombo: false,
    comboItems: [],
    category: 'mainCourse',
    mealType: 'breakfast'
  });
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Combo Builder State
  const [comboBuilderData, setComboBuilderData] = useState({
    name: '',
    description: '',
    selectedItems: [],
    discountPercentage: 10,
    discountType: 'percentage',
    discountAmount: 0
  });

  // Quick Add from Master State
  const [quickAddData, setQuickAddData] = useState({
    selectedItems: [],
    priceAdjustment: 0,
    priceAdjustmentType: 'percentage',
    searchTerm: ''
  });

  // Quick Add from Master State
  const [quickAddItems, setQuickAddItems] = useState([]);
  
  // Copy Daily Menu State
  const [showCopyMenuModal, setShowCopyMenuModal] = useState(false);
  const [copyMenuData, setCopyMenuData] = useState({
    sourceDate: '',
    targetDates: [],
    selectedCategories: []
  });
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Menu categories
  const categories = [
    { key: 'mainCourse', label: 'Main Course', icon: '🍽️' },
    { key: 'appetizers', label: 'Appetizers', icon: '🥟' },
    { key: 'breads', label: 'Breads', icon: '🫓' },
    { key: 'rice', label: 'Rice & Biryani', icon: '🍚' },
    { key: 'curries', label: 'Curries', icon: '🍛' },
    { key: 'tandoor', label: 'Tandoor', icon: '🔥' },
    { key: 'salad', label: 'Salad & Raita', icon: '🥬' },
    { key: 'drinks', label: 'Beverages', icon: '🥤' },
    { key: 'dessert', label: 'Desserts', icon: '🍰' },
    { key: 'combo', label: 'Combo Meals', icon: '🍱' }
  ];

  // Breakfast-specific categories
  const breakfastCategories = [
    { key: 'southIndian', label: 'South Indian', icon: '🥘' },
    { key: 'northIndian', label: 'North Indian', icon: '🫓' },
    { key: 'westIndian', label: 'West Indian', icon: '🍲' },
    { key: 'eastIndian', label: 'East Indian', icon: '🥟' },
    { key: 'healthConscious', label: 'Health-Conscious', icon: '🥗' },
    { key: 'eggsItems', label: 'Eggs Items', icon: '🥚' },
    { key: 'continental', label: 'Continental', icon: '🍳' }
  ];

  // Meal type options
  const mealTypeOptions = [
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'snacks', label: 'Snacks' }
  ];



  // Master menu templates
  const masterMenuTemplates = [
    {
      name: 'Butter Chicken',
      description: 'Creamy tomato-based curry with tender chicken',
      price: 180,
      category: 'mainCourse',
      veg: false,
      calories: 350,
      imageUrl: ''
    },
    {
      name: 'Paneer Tikka',
      description: 'Grilled cottage cheese with Indian spices',
      price: 120,
      category: 'appetizers',
      veg: true,
      calories: 280,
      imageUrl: ''
    },
    {
      name: 'Naan',
      description: 'Soft leavened flatbread',
      price: 30,
      category: 'breads',
      veg: true,
      calories: 150,
      imageUrl: ''
    },
    {
      name: 'Biryani',
      description: 'Fragrant rice dish with spices and meat/vegetables',
      price: 200,
      category: 'rice',
      veg: false,
      calories: 450,
      imageUrl: ''
    },
    {
      name: 'Dal Makhani',
      description: 'Creamy black lentils',
      price: 80,
      category: 'curries',
      veg: true,
      calories: 200,
      imageUrl: ''
    }
  ];

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading data for vendorId:', vendorId);
        
        // Load vendor details
        const vendorData = await mealDeskVendorsService.getVendorById(vendorId);
        console.log('Vendor data loaded:', vendorData);
        
        if (!vendorData) {
          console.error('Vendor not found with ID:', vendorId);
          setVendor(null);
          setLoading(false);
          return;
        }
        
        setVendor(vendorData);

        // Load all menu items for the vendor
        const allMenusQuery = query(
          collection(db, `MealDeskVendors/${vendorId}/menus`),
          orderBy('category')
        );
        
        // Load master menu items
        const masterMenuQuery = query(
          collection(db, `MealDeskVendors/${vendorId}/masterMenu`),
          orderBy('category')
        );
        
        const unsubscribeAllMenus = onSnapshot(allMenusQuery, (snapshot) => {
          const allMenusData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('All menu items loaded:', allMenusData);
          setAllMenuItems(allMenusData);
          
          // Filter by selected date and meal type
          const filteredMenus = allMenusData.filter(item => 
            item.date === selectedDate && item.mealType === selectedMealType
          );
          console.log('Filtered menu items for', selectedDate, selectedMealType, ':', filteredMenus);
          setDailyMenus(filteredMenus);
        });

        const unsubscribeMasterMenu = onSnapshot(masterMenuQuery, (snapshot) => {
          const masterMenuData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('Master menu items loaded:', masterMenuData);
          setMasterMenuItems(masterMenuData);
          setLoading(false);
        });

        return () => {
          unsubscribeAllMenus();
          unsubscribeMasterMenu();
        };
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId, selectedDate, selectedMealType]);

  // Helper functions
  const getItemsByCategory = (category) => {
    return dailyMenus.filter(item => item.category === category);
  };

  const getMasterItemsByCategory = (category) => {
    return masterMenuItems.filter(item => item.category === category);
  };

  // Form handlers
  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      imageUrl: '',
      veg: true,
      calories: '',
      isCombo: false,
      comboItems: [],
      category: 'mainCourse',
      mealType: 'breakfast'
    });
    setErrors({});
    setImageFile(null);
    setImagePreview('');
  };

  const openAddForm = (category) => {
    setActiveCategory(category);
    resetForm();
    setFormData(prev => ({ ...prev, category }));
    setShowAddForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowAddForm(false);
    setShowComboBuilder(false);
    setEditingItem(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Valid price is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.category) newErrors.category = 'Category is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save functions
  const saveMenuItem = async () => {
    if (!validateForm()) return;

    try {
      let imageUrl = formData.imageUrl;
      
      // Upload image if a new file is selected
      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          return; // Stop if image upload failed
        }
      }

      const menuData = {
        ...formData,
        price: parseFloat(formData.price),
        calories: formData.calories ? parseInt(formData.calories) : null,
        date: selectedDate,
        mealType: selectedMealType,
        imageUrl: imageUrl,
        updatedAt: new Date()
      };

      if (editingItem) {
        await updateDoc(doc(db, `MealDeskVendors/${vendorId}/menus`, editingItem.id), menuData);
      } else {
        await addDoc(collection(db, `MealDeskVendors/${vendorId}/menus`), {
          ...menuData,
          createdAt: new Date()
        });
      }

      closeForm();
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Error saving menu item. Please try again.');
    }
  };

  const saveMasterMenuItem = async () => {
    if (!validateForm()) return;

    try {
      let imageUrl = formData.imageUrl;
      
      // Upload image if a new file is selected
      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          return; // Stop if image upload failed
        }
      }

      const masterItemData = {
        ...formData,
        price: parseFloat(formData.price),
        calories: formData.calories ? parseInt(formData.calories) : null,
        imageUrl: imageUrl,
        updatedAt: new Date()
      };

      if (editingItem) {
        await updateDoc(doc(db, `MealDeskVendors/${vendorId}/masterMenu`, editingItem.id), masterItemData);
      } else {
        await addDoc(collection(db, `MealDeskVendors/${vendorId}/masterMenu`), {
          ...masterItemData,
          createdAt: new Date()
        });
      }

      closeForm();
    } catch (error) {
      console.error('Error saving master menu item:', error);
      alert('Error saving master menu item. Please try again.');
    }
  };

  // Delete functions
  const deleteMenuItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      try {
        await deleteDoc(doc(db, `MealDeskVendors/${vendorId}/menus`, itemId));
      } catch (error) {
        console.error('Error deleting menu item:', error);
        alert('Error deleting menu item. Please try again.');
      }
    }
  };

  const deleteMasterMenuItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this master menu item?')) {
      try {
        await deleteDoc(doc(db, `MealDeskVendors/${vendorId}/masterMenu`, itemId));
      } catch (error) {
        console.error('Error deleting master menu item:', error);
        alert('Error deleting master menu item. Please try again.');
      }
    }
  };

  // Image upload
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setUploadingImage(true);
      const storageRef = ref(storage, `menu-images/${vendorId}/${Date.now()}-${imageFile.name}`);
      const snapshot = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setUploadingImage(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadingImage(false);
      alert('Error uploading image. Please try again.');
      return null;
    }
  };

  // Quick add from master
  const quickAddFromMaster = async () => {
    if (quickAddData.selectedItems.length === 0) {
      alert('Please select at least one item from master menu.');
      return;
    }

    try {
      const promises = quickAddData.selectedItems.map(async (item) => {
        let adjustedPrice = item.price;
        
        // Apply price adjustment
        if (quickAddData.priceAdjustmentType === 'percentage') {
          adjustedPrice = item.price * (1 + quickAddData.priceAdjustment / 100);
        } else {
          adjustedPrice = item.price + quickAddData.priceAdjustment;
        }

        const menuData = {
          name: item.name,
          description: item.description,
          price: Math.max(0, adjustedPrice),
          category: item.category,
          veg: item.veg,
          calories: item.calories,
          imageUrl: item.imageUrl,
          date: selectedDate,
          mealType: selectedMealType,
          fromMaster: true,
          masterItemId: item.id,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        return addDoc(collection(db, `MealDeskVendors/${vendorId}/menus`), menuData);
      });

      await Promise.all(promises);
      setShowQuickAdd(false);
      setQuickAddData({ selectedItems: [], priceAdjustment: 0, priceAdjustmentType: 'percentage' });
      alert('Items added to daily menu successfully!');
    } catch (error) {
      console.error('Error adding items from master menu:', error);
      alert('Error adding items from master menu. Please try again.');
    }
  };

  // Add template to master menu
  const addTemplateToMaster = async (template) => {
    try {
      const masterItemData = {
        ...template,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, `MealDeskVendors/${vendorId}/masterMenu`), masterItemData);
      alert('Template added to master menu successfully!');
    } catch (error) {
      console.error('Error adding template to master menu:', error);
      alert('Error adding template to master menu. Please try again.');
    }
  };

  // Save combo meal to master menu
  const saveComboMeal = async () => {
    if (!comboBuilderData.name.trim() || comboBuilderData.selectedItems.length === 0) {
      alert('Please provide a combo name and select at least one item.');
      return;
    }

    try {
      // Calculate total price and calories
      const totalPrice = comboBuilderData.selectedItems.reduce((sum, item) => sum + item.price, 0);
      const totalCalories = comboBuilderData.selectedItems.reduce((sum, item) => sum + (item.calories || 0), 0);
      
      // Calculate final price with discount
      let finalPrice = totalPrice;
      if (comboBuilderData.discountType === 'percentage') {
        finalPrice = totalPrice * (1 - comboBuilderData.discountPercentage / 100);
      } else {
        finalPrice = Math.max(0, totalPrice - comboBuilderData.discountAmount);
      }

      // Check if all items are vegetarian
      const allVeg = comboBuilderData.selectedItems.every(item => item.veg);

      const comboData = {
        name: comboBuilderData.name,
        description: comboBuilderData.description || `Combo meal including ${comboBuilderData.selectedItems.map(item => item.name).join(', ')}`,
        price: Math.round(finalPrice * 100) / 100, // Round to 2 decimal places
        calories: totalCalories,
        category: 'combo',
        veg: allVeg,
        isCombo: true,
        comboItems: comboBuilderData.selectedItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          category: item.category,
          veg: item.veg,
          calories: item.calories
        })),
        discountType: comboBuilderData.discountType,
        discountPercentage: comboBuilderData.discountType === 'percentage' ? comboBuilderData.discountPercentage : 0,
        discountAmount: comboBuilderData.discountType === 'fixed' ? comboBuilderData.discountAmount : 0,
        originalPrice: totalPrice,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, `MealDeskVendors/${vendorId}/masterMenu`), comboData);
      
      // Reset combo builder data
      setComboBuilderData({
        name: '',
        description: '',
        selectedItems: [],
        discountPercentage: 10,
        discountType: 'percentage',
        discountAmount: 0
      });
      
      setShowComboBuilder(false);
      alert('Combo meal created successfully!');
    } catch (error) {
      console.error('Error creating combo meal:', error);
      alert('Error creating combo meal. Please try again.');
    }
  };

  const copyDailyMenu = async () => {
    if (!copyMenuData.sourceDate || copyMenuData.targetDates.length === 0) {
      alert('Please select a source date and at least one target date.');
      return;
    }
    
    try {
      // Get source menu items
      const sourceMenuQuery = query(
        collection(db, `MealDeskVendors/${vendorId}/menus`),
        where('date', '==', copyMenuData.sourceDate)
      );
      
      const sourceSnapshot = await getDocs(sourceMenuQuery);
      const sourceItems = sourceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (sourceItems.length === 0) {
        alert('No menu items found for the selected source date.');
        return;
      }
      
      // Copy items to target dates
      for (const targetDate of copyMenuData.targetDates) {
        for (const item of sourceItems) {
          // Filter by selected categories if specified
          if (copyMenuData.selectedCategories.length > 0 && 
              !copyMenuData.selectedCategories.includes(item.category)) {
            continue;
          }
          
          const newItem = {
            ...item,
            date: targetDate,
            mealType: selectedMealType,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          // Remove the original id to create a new document
          delete newItem.id;
          
          await addDoc(collection(db, `MealDeskVendors/${vendorId}/menus`), newItem);
        }
      }
      
      setCopyMenuData({
        sourceDate: '',
        targetDates: [],
        selectedCategories: []
      });
      setShowCopyMenuModal(false);
      alert(`Menu copied successfully to ${copyMenuData.targetDates.length} date(s)!`);
    } catch (error) {
      console.error('Error copying menu:', error);
      alert('Error copying menu. Please try again.');
    }
  };

  const toggleDescription = (itemId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Vendor not found
  if (!vendor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500">Vendor not found</p>
          <button
            onClick={() => navigate('/kerbletmeal/vendors')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Vendors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/kerbletmeal/vendors')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {vendor?.name}
              </h1>
              <p className="text-lg text-gray-600">
                Menu Editor
              </p>
            </div>
          </div>


        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900 flex items-center">
                <StarIcon className="h-5 w-5 mr-2" />
                Menu Management Guide
              </h3>
              <button
                onClick={() => {
                  const helpSection = document.getElementById('help-section');
                  if (helpSection) {
                    helpSection.classList.toggle('hidden');
                  }
                }}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Toggle Help
              </button>
            </div>
            <div id="help-section" className="space-y-4">
              <div className="grid md:grid-cols-3 gap-6">
                {/* How to add menu items to Master Menu */}
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Adding Items to Master Menu
                  </h4>
                  <ol className="text-sm text-gray-700 space-y-2">
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">1.</span>
                      <span>Go to the <strong>Master Menu</strong> tab</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">2.</span>
                      <span>Select the appropriate <strong>Meal Type</strong> (Breakfast, Lunch, etc.)</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">3.</span>
                      <span>Click <strong>"Add Master Item"</strong> button</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">4.</span>
                      <span>Fill in item details and select the appropriate <strong>Category</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">5.</span>
                      <span>Click <strong>"Save Item"</strong> to add to Master Menu</span>
                    </li>
                  </ol>
                </div>

                {/* How to use Quick Add from Master Menu */}
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Quick Add from Master Menu
                  </h4>
                  <ol className="text-sm text-gray-700 space-y-2">
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">1.</span>
                      <span>Go to the <strong>Daily Menu</strong> tab</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">2.</span>
                      <span>Select your desired <strong>Date</strong> and <strong>Meal Type</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">3.</span>
                      <span>Click <strong>"Quick Add from Master Menu"</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">4.</span>
                      <span>Search and select items from your Master Menu</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">5.</span>
                      <span>Adjust prices if needed and click <strong>"Add to Daily Menu"</strong></span>
                    </li>
                  </ol>
                </div>

                {/* How to copy Daily Menu */}
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                    <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                    Copy Daily Menu
                  </h4>
                  <ol className="text-sm text-gray-700 space-y-2">
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">1.</span>
                      <span>Go to the <strong>Daily Menu</strong> tab</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">2.</span>
                      <span>Click <strong>"Copy Daily Menu"</strong> button</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">3.</span>
                      <span>Select the <strong>source date</strong> to copy from</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">4.</span>
                      <span>Choose <strong>target dates</strong> to copy to</span>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-blue-600 mr-2">5.</span>
                      <span>Optionally filter by <strong>categories</strong> and click <strong>"Copy Menu"</strong></span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('daily')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'daily'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5" />
                    <span>Daily Menu</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('master')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'master'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <BookmarkIcon className="h-5 w-5" />
                    <span>Master Menu</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Daily Menu Content */}
          {activeTab === 'daily' && (
            <div className="bg-white rounded-lg shadow mb-6">
              {/* Quick Add from Master Button and Date/Meal Type Selector */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Daily Menu</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowQuickAdd(true)}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    >
                      <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                      Quick Add from Master
                    </button>
                    <button
                      onClick={() => setShowCopyMenuModal(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                      Copy Daily Menu
                    </button>
                  </div>
                </div>
                
                {/* Date and Meal Type Selector */}
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <label className="text-sm font-medium text-gray-700">Date:</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-gray-400" />
                    <label className="text-sm font-medium text-gray-700">Meal Type:</label>
                    <select
                      value={selectedMealType}
                      onChange={(e) => {
                        setSelectedMealType(e.target.value);
                        // Reset active category when meal type changes
                        if (e.target.value === 'breakfast') {
                          setActiveCategory('southIndian');
                        } else {
                          setActiveCategory('mainCourse');
                        }
                      }}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {mealTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Category Navigation */}
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 mb-3">Menu Categories</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {(selectedMealType === 'breakfast' ? breakfastCategories : categories).map((category) => {
                    const itemCount = getItemsByCategory(category.key).length;
                    const isActive = activeCategory === category.key;
                    return (
                      <button
                        key={category.key}
                        onClick={() => setActiveCategory(category.key)}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          isActive
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-2xl mb-1">{category.icon}</span>
                        <span className="text-xs font-medium text-center">{category.label}</span>
                        <span className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive
                            ? 'bg-blue-100 text-blue-900'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {itemCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Breakfast Categories Advisory Text */}
                {selectedMealType === 'breakfast' && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Breakfast Category Guide:</strong><br />
                      <strong>South Indian:</strong> Idli, dosa, vada, sambar, upma, pesarattu, and uttapam are common, often served with chutneys and sambar.<br />
                      <strong>North Indian:</strong> Parathas (stuffed flatbreads), puri-sabzi, chole bhature, and lassi are popular.<br />
                      <strong>West Indian:</strong> Poha, upma, dhokla, thepla, khandvi, and jalebi are frequently enjoyed.<br />
                      <strong>East Indian:</strong> Luchi (similar to puri), ghugni, and aloo dum are typical breakfast items.
                    </p>
                  </div>
                )}
              </div>

              {/* Category Content */}
              <div className="p-6">
                {(selectedMealType === 'breakfast' ? breakfastCategories : categories).map((category) => {
                  const items = getItemsByCategory(category.key);
                  const isActive = activeCategory === category.key;
                  
                  if (!isActive) return null;
                  
                  return (
                    <div key={category.key}>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{category.icon}</span>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">{category.label}</h3>
                            <p className="text-sm text-gray-500">{items.length} items available</p>
                          </div>
                        </div>
                      </div>

                      {items.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="text-gray-400 mb-4">
                            <CalendarIcon className="h-12 w-12 mx-auto" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No items yet</h3>
                          <p className="text-gray-500 mb-4">Use "Quick Add from Master" to add items to your daily menu.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map((item) => (
                            <div key={item.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                              {/* Action buttons */}
                              <div className="flex justify-end space-x-1 p-3">
                                <button
                                  onClick={() => {
                                    setEditingItem(item);
                                    setFormData({
                                      name: item.name || '',
                                      price: item.price || '',
                                      description: item.description || '',
                                      imageUrl: item.imageUrl || '',
                                      veg: item.veg !== undefined ? item.veg : true,
                                      calories: item.calories || '',
                                      category: item.category,
                                      isCombo: item.isCombo || false,
                                      comboItems: item.comboItems || []
                                    });
                                    setShowAddForm(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => deleteMenuItem(item.id)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Image - Full width at top */}
                              <div className="w-full">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="w-full h-48 object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-400 text-sm">No Image</span>
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="p-4">
                                {/* Item Name */}
                                <div className="flex items-center justify-center mb-2">
                                  <h4 className="font-medium text-gray-900">{item.name}</h4>
                                  {item.isCombo && (
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      <ShoppingCartIcon className="h-3 w-3 mr-1" />
                                      Combo
                                    </span>
                                  )}
                                </div>

                                {/* Description with Read More */}
                                <div className="mb-3">
                                  <p className="text-sm text-gray-600 text-center">
                                    {expandedDescriptions[item.id] 
                                      ? item.description 
                                      : item.description?.length > 100 
                                        ? `${item.description.substring(0, 100)}...` 
                                        : item.description
                                    }
                                  </p>
                                  {item.description?.length > 100 && (
                                    <button
                                      onClick={() => toggleDescription(item.id)}
                                      className="text-blue-600 hover:text-blue-800 text-xs mt-1 block mx-auto"
                                    >
                                      {expandedDescriptions[item.id] ? 'Read Less' : 'Read More'}
                                    </button>
                                  )}
                                </div>

                                {/* Price, Veg/Non-Veg Tag, and Calories in single row */}
                                <div className="flex items-center justify-between">
                                  <span className="text-lg font-bold text-green-600">₹{item.price}</span>
                                  {item.veg ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <HeartIcon className="h-3 w-3 mr-1" />
                                      Veg
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Non-Veg
                                    </span>
                                  )}
                                  {item.calories && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      <FireIcon className="h-3 w-3 mr-1" />
                                      {item.calories} cal
                                    </span>
                                  )}
                                </div>

                                {/* Combo Items Display */}
                                {item.isCombo && item.comboItems && item.comboItems.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs font-medium text-gray-700 mb-2">Combo Includes:</div>
                                    <div className="space-y-1">
                                      {item.comboItems.map((comboItem, index) => (
                                        <div key={index} className="flex items-center justify-between text-xs">
                                          <span className="text-gray-600">• {comboItem.name}</span>
                                          <span className="text-gray-500">₹{comboItem.price}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {item.originalPrice && item.originalPrice > item.price && (
                                      <div className="mt-2 pt-2 border-t border-gray-100">
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-500 line-through">Original: ₹{item.originalPrice}</span>
                                          <span className="text-green-600 font-medium">Save: ₹{(item.originalPrice - item.price).toFixed(2)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Master Menu Content */}
          {activeTab === 'master' && (
            <div className="bg-white rounded-lg shadow mb-6">
              {/* Master Menu Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">Master Menu</h2>
                  <div className="flex items-center space-x-4">
                    {/* Meal Type Selector */}
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                      <label className="text-sm font-medium text-gray-700">Meal Type:</label>
                      <select
                        value={selectedMealType}
                        onChange={(e) => {
                          setSelectedMealType(e.target.value);
                          // Reset active category when meal type changes
                          if (e.target.value === 'breakfast') {
                            setActiveCategory('southIndian');
                          } else {
                            setActiveCategory('mainCourse');
                          }
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {mealTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Master Item
                      </button>
                      <button
                        onClick={() => setShowComboBuilder(true)}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      >
                        <ShoppingCartIcon className="h-4 w-4 mr-2" />
                        Create Combo Meal
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Master Menu Categories */}
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 mb-3">Master Menu Categories</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {(selectedMealType === 'breakfast' ? breakfastCategories : categories).map((category) => {
                    const itemCount = getMasterItemsByCategory(category.key).length;
                    const isActive = activeCategory === category.key;
                    return (
                      <button
                        key={category.key}
                        onClick={() => setActiveCategory(category.key)}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          isActive
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-2xl mb-1">{category.icon}</span>
                        <span className="text-xs font-medium text-center">{category.label}</span>
                        <span className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive
                            ? 'bg-blue-100 text-blue-900'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {itemCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Master Menu Content */}
              <div className="p-6">
                {(selectedMealType === 'breakfast' ? breakfastCategories : categories).map((category) => {
                  const items = getMasterItemsByCategory(category.key);
                  const isActive = activeCategory === category.key;
                  
                  if (!isActive) return null;
                  
                  return (
                    <div key={category.key}>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{category.icon}</span>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">{category.label}</h3>
                            <p className="text-sm text-gray-500">{items.length} master items available</p>
                          </div>
                        </div>
                        {category.key !== 'combo' && (
                          <button
                            onClick={() => openAddForm(category.key)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add {category.label}
                          </button>
                        )}
                      </div>

                      {items.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="text-gray-400 mb-4">
                            <BookmarkIcon className="h-12 w-12 mx-auto" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No master items yet</h3>
                          <p className="text-gray-500 mb-4">Create your first master menu item to get started.</p>
                          <button
                            onClick={() => openAddForm(category.key)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add First Item
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map((item) => (
                            <div key={item.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                              {/* Action buttons */}
                              <div className="flex justify-end space-x-1 p-3">
                                <button
                                  onClick={() => {
                                    setEditingItem(item);
                                    setFormData({
                                      name: item.name || '',
                                      price: item.price || '',
                                      description: item.description || '',
                                      imageUrl: item.imageUrl || '',
                                      veg: item.veg !== undefined ? item.veg : true,
                                      calories: item.calories || '',
                                      category: item.category,
                                      isCombo: item.isCombo || false,
                                      comboItems: item.comboItems || []
                                    });
                                    setShowAddForm(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => deleteMasterMenuItem(item.id)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Image - Full width at top */}
                              <div className="w-full">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="w-full h-48 object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-400 text-sm">No Image</span>
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="p-4">
                                {/* Item Name */}
                                <div className="flex items-center justify-center mb-2">
                                  <h4 className="font-medium text-gray-900">{item.name}</h4>
                                  {item.isCombo && (
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      <ShoppingCartIcon className="h-3 w-3 mr-1" />
                                      Combo
                                    </span>
                                  )}
                                </div>

                                {/* Description with Read More */}
                                <div className="mb-3">
                                  <p className="text-sm text-gray-600 text-center">
                                    {expandedDescriptions[item.id] 
                                      ? item.description 
                                      : item.description?.length > 100 
                                        ? `${item.description.substring(0, 100)}...` 
                                        : item.description
                                    }
                                  </p>
                                  {item.description?.length > 100 && (
                                    <button
                                      onClick={() => toggleDescription(item.id)}
                                      className="text-blue-600 hover:text-blue-800 text-xs mt-1 block mx-auto"
                                    >
                                      {expandedDescriptions[item.id] ? 'Read Less' : 'Read More'}
                                    </button>
                                  )}
                                </div>

                                {/* Price, Veg/Non-Veg Tag, and Calories in single row */}
                                <div className="flex items-center justify-between">
                                  <span className="text-lg font-bold text-green-600">₹{item.price}</span>
                                  {item.veg ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <HeartIcon className="h-3 w-3 mr-1" />
                                      Veg
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Non-Veg
                                    </span>
                                  )}
                                  {item.calories && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      <FireIcon className="h-3 w-3 mr-1" />
                                      {item.calories} cal
                                    </span>
                                  )}
                                </div>

                                {/* Combo Items Display */}
                                {item.isCombo && item.comboItems && item.comboItems.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs font-medium text-gray-700 mb-2">Combo Includes:</div>
                                    <div className="space-y-1">
                                      {item.comboItems.map((comboItem, index) => (
                                        <div key={index} className="flex items-center justify-between text-xs">
                                          <span className="text-gray-600">• {comboItem.name}</span>
                                          <span className="text-gray-500">₹{comboItem.price}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {item.originalPrice && item.originalPrice > item.price && (
                                      <div className="mt-2 pt-2 border-t border-gray-100">
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-500 line-through">Original: ₹{item.originalPrice}</span>
                                          <span className="text-green-600 font-medium">Save: ₹{(item.originalPrice - item.price).toFixed(2)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeForm}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Modal header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
                    </h2>
                    <button
                      onClick={closeForm}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <form className="space-y-6">
                    {/* Item Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Item Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.name ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter item name"
                      />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Price (₹) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.price ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter price"
                      />
                      {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={3}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.description ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter item description"
                      />
                      {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                    </div>

                    {/* Meal Type for Category Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Meal Type *
                      </label>
                      <select
                        value={formData.mealType || selectedMealType}
                        onChange={(e) => {
                          handleInputChange('mealType', e.target.value);
                          // Reset category when meal type changes
                          if (e.target.value === 'breakfast') {
                            handleInputChange('category', 'southIndian');
                          } else {
                            handleInputChange('category', 'mainCourse');
                          }
                        }}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.mealType ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      >
                        {mealTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.mealType && <p className="mt-1 text-sm text-red-600">{errors.mealType}</p>}
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Category *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.category ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      >
                        {(formData.mealType || selectedMealType) === 'breakfast' 
                          ? breakfastCategories.map((category) => (
                              <option key={category.key} value={category.key}>
                                {category.label}
                              </option>
                            ))
                          : categories.filter(category => category.key !== 'combo').map((category) => (
                              <option key={category.key} value={category.key}>
                                {category.label}
                              </option>
                            ))
                        }
                      </select>
                      {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                    </div>

                    {/* Veg/Non-Veg */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.veg}
                          onChange={(e) => handleInputChange('veg', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Vegetarian</span>
                      </label>
                    </div>

                                         {/* Calories */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700">
                         Calories
                       </label>
                       <input
                         type="number"
                         min="0"
                         value={formData.calories}
                         onChange={(e) => handleInputChange('calories', e.target.value)}
                         className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                         placeholder="Enter calories"
                       />
                     </div>

                     {/* Image Upload */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Item Image
                       </label>
                       <div className="space-y-3">
                         {/* Current Image Preview */}
                         {(formData.imageUrl || imagePreview) && (
                           <div className="flex items-center space-x-3">
                             <img
                               src={imagePreview || formData.imageUrl}
                               alt="Preview"
                               className="w-20 h-20 object-cover rounded-lg border"
                               onError={(e) => {
                                 e.target.style.display = 'none';
                               }}
                             />
                             <button
                               type="button"
                               onClick={() => {
                                 setImageFile(null);
                                 setImagePreview('');
                                 handleInputChange('imageUrl', '');
                               }}
                               className="text-red-600 hover:text-red-800 text-sm"
                             >
                               Remove Image
                             </button>
                           </div>
                         )}
                         
                         {/* File Upload */}
                         <div className="flex items-center space-x-3">
                           <input
                             type="file"
                             accept="image/*"
                             onChange={(e) => {
                               const file = e.target.files[0];
                               if (file) {
                                 setImageFile(file);
                                 const reader = new FileReader();
                                 reader.onload = (e) => setImagePreview(e.target.result);
                                 reader.readAsDataURL(file);
                               }
                             }}
                             className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                           />
                         </div>
                         
                         {/* URL Input */}
                         <div className="flex items-center space-x-2">
                           <span className="text-sm text-gray-500">Or enter image URL:</span>
                           <input
                             type="url"
                             value={formData.imageUrl}
                             onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                             className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                             placeholder="https://example.com/image.jpg"
                           />
                         </div>
                       </div>
                     </div>
                  </form>
                </div>

                {/* Modal footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeForm}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={activeTab === 'master' ? saveMasterMenuItem : saveMenuItem}
                      disabled={uploadingImage}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingImage ? 'Uploading...' : (editingItem ? 'Update Item' : 'Add Item')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowQuickAdd(false)}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Modal header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Quick Add from Master Menu</h2>
                    <button
                      onClick={() => setShowQuickAdd(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-6">
                    {/* Price Adjustment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price Adjustment
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={quickAddData.priceAdjustmentType}
                          onChange={(e) => setQuickAddData(prev => ({ ...prev, priceAdjustmentType: e.target.value }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (₹)</option>
                        </select>
                        <input
                          type="number"
                          value={quickAddData.priceAdjustment}
                          onChange={(e) => setQuickAddData(prev => ({ ...prev, priceAdjustment: parseFloat(e.target.value) || 0 }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Master Menu Items */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Items from Master Menu
                      </label>
                      
                      {/* Search Input */}
                      <div className="mb-3">
                        <input
                          type="text"
                          placeholder="Search items by name..."
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={quickAddData.searchTerm || ''}
                          onChange={(e) => setQuickAddData(prev => ({ ...prev, searchTerm: e.target.value }))}
                        />
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {masterMenuItems
                          .filter(item => 
                            !quickAddData.searchTerm || 
                            item.name.toLowerCase().includes(quickAddData.searchTerm.toLowerCase()) ||
                            item.category.toLowerCase().includes(quickAddData.searchTerm.toLowerCase())
                          )
                          .map((item) => (
                           <label key={item.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                             <input
                               type="checkbox"
                               checked={quickAddData.selectedItems.some(selected => selected.id === item.id)}
                               onChange={(e) => {
                                 if (e.target.checked) {
                                   setQuickAddData(prev => ({
                                     ...prev,
                                     selectedItems: [...prev.selectedItems, item]
                                   }));
                                 } else {
                                   setQuickAddData(prev => ({
                                     ...prev,
                                     selectedItems: prev.selectedItems.filter(selected => selected.id !== item.id)
                                   }));
                                 }
                               }}
                               className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                             />
                             <div className="ml-3 flex items-center space-x-3 flex-1">
                               {/* Image */}
                               <div className="flex-shrink-0">
                                 {item.imageUrl ? (
                                   <img
                                     src={item.imageUrl}
                                     alt={item.name}
                                     className="w-12 h-12 object-cover rounded"
                                     onError={(e) => {
                                       e.target.style.display = 'none';
                                     }}
                                   />
                                 ) : (
                                   <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                     <span className="text-gray-400 text-xs">No Image</span>
                                   </div>
                                 )}
                               </div>
                               {/* Content */}
                               <div className="flex-1 min-w-0">
                                 <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                 <div className="text-xs text-gray-500">₹{item.price} • {item.category}</div>
                               </div>
                             </div>
                           </label>
                         ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={quickAddFromMaster}
                      disabled={quickAddData.selectedItems.length === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add to Daily Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Combo Builder Modal */}
      {showComboBuilder && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowComboBuilder(false)}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Modal header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Create Combo Meal</h2>
                    <button
                      onClick={() => setShowComboBuilder(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-6">
                    {/* Combo Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Combo Name *
                      </label>
                      <input
                        type="text"
                        value={comboBuilderData.name}
                        onChange={(e) => setComboBuilderData(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter combo name"
                      />
                    </div>

                    {/* Combo Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={comboBuilderData.description}
                        onChange={(e) => setComboBuilderData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter combo description"
                      />
                    </div>

                    {/* Discount Settings */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Settings
                      </label>
                      <div className="space-y-3">
                        <div className="flex space-x-2">
                          <select
                            value={comboBuilderData.discountType}
                            onChange={(e) => setComboBuilderData(prev => ({ ...prev, discountType: e.target.value }))}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="percentage">Percentage (%)</option>
                            <option value="fixed">Fixed Amount (₹)</option>
                          </select>
                          <input
                            type="number"
                            value={comboBuilderData.discountType === 'percentage' ? comboBuilderData.discountPercentage : comboBuilderData.discountAmount}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              if (comboBuilderData.discountType === 'percentage') {
                                setComboBuilderData(prev => ({ ...prev, discountPercentage: value }));
                              } else {
                                setComboBuilderData(prev => ({ ...prev, discountAmount: value }));
                              }
                            }}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Master Menu Items Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Items for Combo
                      </label>
                      <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {masterMenuItems.map((item) => (
                          <label key={item.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={comboBuilderData.selectedItems.some(selected => selected.id === item.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setComboBuilderData(prev => ({
                                    ...prev,
                                    selectedItems: [...prev.selectedItems, item]
                                  }));
                                } else {
                                  setComboBuilderData(prev => ({
                                    ...prev,
                                    selectedItems: prev.selectedItems.filter(selected => selected.id !== item.id)
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="ml-3 flex items-center space-x-3 flex-1">
                              {/* Image */}
                              <div className="flex-shrink-0">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                    <span className="text-gray-400 text-xs">No Image</span>
                                  </div>
                                )}
                              </div>
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                <div className="text-xs text-gray-500">₹{item.price} • {item.category}</div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Combo Summary */}
                    {comboBuilderData.selectedItems.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Combo Summary</h4>
                        <div className="space-y-2">
                          {comboBuilderData.selectedItems.map((item, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600">{item.name}</span>
                              <span className="text-gray-900">₹{item.price}</span>
                            </div>
                          ))}
                          <div className="border-t pt-2 mt-2">
                            <div className="flex justify-between text-sm font-medium">
                              <span className="text-gray-900">Total Price:</span>
                              <span className="text-green-600">
                                ₹{comboBuilderData.selectedItems.reduce((sum, item) => sum + item.price, 0)}
                              </span>
                            </div>
                            {comboBuilderData.discountType === 'percentage' && comboBuilderData.discountPercentage > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Discount ({comboBuilderData.discountPercentage}%):</span>
                                <span className="text-red-600">
                                  -₹{(comboBuilderData.selectedItems.reduce((sum, item) => sum + item.price, 0) * comboBuilderData.discountPercentage / 100).toFixed(2)}
                                </span>
                              </div>
                            )}
                            {comboBuilderData.discountType === 'fixed' && comboBuilderData.discountAmount > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Discount:</span>
                                <span className="text-red-600">-₹{comboBuilderData.discountAmount}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm font-bold">
                              <span className="text-gray-900">Final Price:</span>
                              <span className="text-green-600">
                                ₹{(() => {
                                  const totalPrice = comboBuilderData.selectedItems.reduce((sum, item) => sum + item.price, 0);
                                  let finalPrice = totalPrice;
                                  if (comboBuilderData.discountType === 'percentage') {
                                    finalPrice = totalPrice * (1 - comboBuilderData.discountPercentage / 100);
                                  } else {
                                    finalPrice = Math.max(0, totalPrice - comboBuilderData.discountAmount);
                                  }
                                  return finalPrice.toFixed(2);
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowComboBuilder(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveComboMeal}
                      disabled={comboBuilderData.selectedItems.length === 0 || !comboBuilderData.name.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Create Combo Meal
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Daily Menu Modal */}
      {showCopyMenuModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCopyMenuModal(false)}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Modal header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Copy Daily Menu</h2>
                    <button
                      onClick={() => setShowCopyMenuModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-6">
                    {/* Source Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Source Date *
                      </label>
                      <input
                        type="date"
                        value={copyMenuData.sourceDate}
                        onChange={(e) => setCopyMenuData(prev => ({ ...prev, sourceDate: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Target Dates */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Dates *
                      </label>
                      <div className="space-y-2">
                        {[1, 2, 3, 4, 5, 6, 7].map((daysAhead) => {
                          const targetDate = new Date();
                          targetDate.setDate(targetDate.getDate() + daysAhead);
                          const dateString = targetDate.toISOString().split('T')[0];
                          return (
                            <label key={daysAhead} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={copyMenuData.targetDates.includes(dateString)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCopyMenuData(prev => ({
                                      ...prev,
                                      targetDates: [...prev.targetDates, dateString]
                                    }));
                                  } else {
                                    setCopyMenuData(prev => ({
                                      ...prev,
                                      targetDates: prev.targetDates.filter(date => date !== dateString)
                                    }));
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {targetDate.toLocaleDateString('en-US', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Category Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categories to Copy (Optional)
                      </label>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {categories.map((category) => (
                          <label key={category.key} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={copyMenuData.selectedCategories.includes(category.key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCopyMenuData(prev => ({
                                    ...prev,
                                    selectedCategories: [...prev.selectedCategories, category.key]
                                  }));
                                } else {
                                  setCopyMenuData(prev => ({
                                    ...prev,
                                    selectedCategories: prev.selectedCategories.filter(cat => cat !== category.key)
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{category.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Leave unchecked to copy all categories
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCopyMenuModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={copyDailyMenu}
                      disabled={!copyMenuData.sourceDate || copyMenuData.targetDates.length === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Copy Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuEditor; 